import { useState } from "react";
import {
  useInferenceThread,
  useInterrogate,
  useRecordInferenceFeedback,
  type InterrogateResult,
  type InferenceCitation,
} from "../hooks/useInferenceChat";

/**
 * InferenceChat — interrogate the rationale behind ANY inference, ground the
 * answer in the real evidence corpus, surface evidence gaps (and prompt the
 * user to feed evidence), and capture feedback for retuning.
 *
 * Reusable: a card passes the inference's own on-screen rationale as `context`
 * plus a stable `surface` + `refId` so the thread persists per inference.
 */
export default function InferenceChat({
  caseId,
  surface,
  refId,
  label,
  context,
  onClose,
  intro,
  placeholder,
}: {
  caseId?: string;
  surface: string;
  refId?: string | null;
  label: string;
  context?: string;
  onClose?: () => void;
  intro?: string;
  placeholder?: string;
}) {
  const { threadId, messages } = useInferenceThread({ caseId, surface, refId, label });
  const interrogate = useInterrogate(caseId, threadId);
  const feedback = useRecordInferenceFeedback(caseId);

  const [question, setQuestion] = useState("");
  const [evidence, setEvidence] = useState("");
  const [note, setNote] = useState("");
  const [last, setLast] = useState<InterrogateResult | null>(null);
  const [fbDone, setFbDone] = useState<string | null>(null);

  const msgs = messages.data ?? [];
  const busy = interrogate.isPending;

  function ask(q: string, considered?: string) {
    const text = q.trim();
    if (!text || busy) return;
    interrogate.mutate(
      { surface, refId, label, context, question: text, considered },
      { onSuccess: (r) => { setLast(r); setQuestion(""); } },
    );
  }

  function feedEvidence() {
    const e = evidence.trim();
    if (!e) return;
    // 1) re-ask, having the model weigh the supplied evidence
    ask(`I can provide this evidence: ${e}. Does it change the assessment?`, e);
    // 2) flag it so the team can ingest the actual exhibit
    feedback.mutate(
      { surface, refId, label, rating: "flag", note: `Evidence to add: ${e}`, threadId },
      { onSuccess: () => setFbDone("flagged — saved for ingestion") },
    );
    setEvidence("");
  }

  function rate(rating: "up" | "down" | "flag") {
    feedback.mutate(
      { surface, refId, label, rating, note: note.trim() || undefined, threadId },
      { onSuccess: () => { setFbDone(rating === "up" ? "thanks — noted" : "noted for review"); setNote(""); } },
    );
  }

  return (
    <div className="mt-2 flex flex-col gap-3 rounded border border-blue-bright/40 bg-iron/60 p-3 backdrop-blur-sm">
      <div className="flex items-baseline justify-between">
        <span className="font-display text-sm font-light uppercase tracking-[0.12em] text-blue-bright">
          interrogate · {label}
        </span>
        {onClose && (
          <button type="button" onClick={onClose} className="font-display text-sm text-bone-mute/60 hover:text-bone-dim">close</button>
        )}
      </div>

      {context && (
        <details className="group">
          <summary className="cursor-pointer list-none font-display text-sm text-bone-mute/70 hover:text-bone-dim">
            what it decided &amp; why ▸
          </summary>
          <p className="mt-1 whitespace-pre-wrap rounded border border-steel-dark/60 bg-black/20 p-2 font-display text-sm leading-snug text-bone-dim">
            {context}
          </p>
        </details>
      )}

      {/* transcript */}
      <div className="flex max-h-72 flex-col gap-2 overflow-y-auto">
        {msgs.length === 0 && !busy && (
          <p className="font-display text-sm text-bone-mute/70">
            {intro ?? "Ask why it reached this conclusion, what criteria it used, or whether it weighed something specific."}
          </p>
        )}
        {msgs.map((m) => (
          <div key={m.id} className={`rounded border px-3 py-2 ${
            m.role === "user" ? "border-steel-dark/60 bg-iron-2/40" : "border-blue-bright/30 bg-black/20"
          }`}>
            <p className="mb-0.5 font-display text-sm font-light uppercase tracking-[0.1em] text-bone-mute/60">
              {m.role === "user" ? "you" : "the engine"}
            </p>
            <p className="whitespace-pre-wrap font-display text-sm leading-snug text-bone-dim">{m.content}</p>
            {m.citations && m.citations.length > 0 && (
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {m.citations.map((c) => <Cite key={c.chunk_id} c={c} />)}
              </div>
            )}
          </div>
        ))}
        {busy && <p className="font-display text-sm text-bone-mute">checking the record…</p>}
      </div>

      {interrogate.error && (
        <p className="font-display text-sm text-ember-hot">{interrogate.error.message}</p>
      )}

      {/* evidence-gap prompt */}
      {last?.evidence_gap && (
        <div className="flex flex-col gap-2 rounded border border-ember-bright/50 bg-ember/15 p-2.5">
          <p className="font-display text-sm leading-snug text-ember-hot">
            Evidence gap: {last.needed_evidence || "the record doesn't substantiate this yet."}
          </p>
          <p className="font-display text-sm text-bone-mute/80">Describe or paste the evidence and I'll weigh it (and flag it to be added):</p>
          <textarea value={evidence} onChange={(e) => setEvidence(e.target.value)} rows={2}
            placeholder="e.g. the executed 2019 trust amendment shows…"
            className="w-full resize-none rounded border border-steel-dark bg-iron px-2.5 py-1.5 font-display text-sm text-bone placeholder:text-bone-mute/40 focus:border-ember-bright/60 focus:outline-none" />
          <button type="button" disabled={!evidence.trim() || busy} onClick={feedEvidence}
            className="self-start border border-ember-bright/60 bg-ember/30 px-3 py-1.5 font-display text-sm font-light uppercase tracking-[0.1em] text-bone transition-colors hover:bg-ember/50 disabled:opacity-40">
            feed this evidence
          </button>
        </div>
      )}

      {last?.suggests_rerun && last.rerun_reason && (
        <p className="font-display text-sm text-bone-mute/70">
          ↻ A fresh run is suggested: {last.rerun_reason}
        </p>
      )}

      {/* ask box */}
      <div className="flex items-center gap-2">
        <input type="text" value={question} onChange={(e) => setQuestion(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") ask(question); }}
          placeholder={placeholder ?? "Ask about this decision…"}
          className="min-w-0 flex-1 rounded border border-steel-dark bg-iron px-3 py-1.5 font-display text-sm text-bone placeholder:text-bone-mute/40 focus:border-blue-bright/70 focus:outline-none" />
        <button type="button" disabled={!question.trim() || busy || !threadId} onClick={() => ask(question)}
          className="shrink-0 border border-blue-bright/50 bg-iron-2/70 px-4 py-1.5 font-display text-sm font-light uppercase tracking-[0.1em] text-bone transition-colors hover:bg-iron-2 disabled:opacity-40">
          ask
        </button>
      </div>

      {/* feedback bar */}
      <div className="flex flex-wrap items-center gap-2 border-t border-steel-dark/60 pt-2">
        <span className="font-display text-sm text-bone-mute/60">was this right?</span>
        <button type="button" onClick={() => rate("up")} className="rounded border border-steel-dark px-2 py-0.5 font-display text-sm text-bone-mute hover:border-blue-bright/60 hover:text-blue-bright">👍</button>
        <button type="button" onClick={() => rate("down")} className="rounded border border-steel-dark px-2 py-0.5 font-display text-sm text-bone-mute hover:border-ember-bright/60 hover:text-ember-hot">👎</button>
        <button type="button" onClick={() => rate("flag")} className="rounded border border-steel-dark px-2 py-0.5 font-display text-sm text-bone-mute hover:border-ember-bright/60 hover:text-ember-hot">⚑ flag</button>
        <input type="text" value={note} onChange={(e) => setNote(e.target.value)} placeholder="optional note (what it missed)"
          className="min-w-0 flex-1 rounded border border-steel-dark bg-iron px-2.5 py-1 font-display text-sm text-bone placeholder:text-bone-mute/40 focus:border-blue-bright/70 focus:outline-none" />
        {fbDone && <span className="font-display text-sm text-blue-bright/80">{fbDone}</span>}
      </div>
    </div>
  );
}

function Cite({ c }: { c: InferenceCitation }) {
  return (
    <span title={c.snippet}
      className="rounded border border-steel-dark/70 bg-black/30 px-2 py-0.5 font-display text-sm text-bone-mute/80">
      {c.source_label}{c.occurred_on ? ` · ${c.occurred_on}` : ""}
    </span>
  );
}
